/**
 * React hook for gateway connection lifecycle.
 *
 * Creates the GatewayClient, connects to the gateway, and manages
 * connection state in the Zustand store. Cleans up on unmount.
 *
 * URL + auth resolution:
 *
 * - **Dev** (`VITE_LEXSERVER_WS_URL` unset): connect to
 *   `wss?://${window.location.host}/ws` and rely on the Vite dev-server
 *   proxy to forward to the local lexruntime gateway. No subprotocol —
 *   the Vite proxy is the only hop, so no routing JWT is needed.
 *
 * - **Production** (`VITE_LEXSERVER_WS_URL` set, e.g.
 *   `wss://srv.lexiun.ai/ws`): connect cross-origin to lexserver's WS
 *   proxy and pass the Lexiun JWT in the
 *   `Sec-WebSocket-Protocol: lexiun-jwt.<token>` header so the proxy
 *   can verify the JWT and resolve the per-user lexruntime BEFORE
 *   accepting the upgrade. See lexserver/src/services/runtime-ws-proxy.ts
 *   and ai-docs/per-user-runtime-proxy-plan.md (P2 / P2.B).
 *
 * The subprotocol JWT is fetched once at mount via `getToken()`; the
 * existing in-band `connect` frame still authenticates against the
 * lexruntime gateway as before. Token refresh on reconnect is a
 * deferred follow-up — short-lived sessions are unaffected.
 */

import { useEffect, useRef, useState } from "react";
import { GatewayClient } from "@/lib/gateway-client";
import { createGatewayRpc } from "@/lib/gateway-rpc";
import { useGatewayStore, type RuntimeState } from "@/stores/useGatewayStore";
import { dispatchEvent } from "@/hooks/useEventStream";
import { clearLlmStatusCache } from "@/hooks/useLlmStatus";
import { getToken } from "@/lib/lexiun-jwt";
import { clientLog } from "@/lib/client-log";

interface UseGatewayOptions {
  /** WebSocket URL. Default: derived from VITE_LEXSERVER_WS_URL or
   *  window.location (via Vite proxy). */
  url?: string;
  /** Gateway auth token. In dev: "lexdev". In prod: obtained from env/config. */
  gatewayToken?: string | null;
  /** Auto-connect when mounted. Default: true. */
  autoConnect?: boolean;
}

function resolveGatewayUrl(): string {
  // Production override: explicit lexserver WS URL (e.g.
  // `wss://srv.lexiun.ai/ws`). When set, the SPA opens a direct
  // cross-origin WS to lexserver's runtime-WS proxy. When unset, fall
  // back to the legacy same-origin path that's served by the Vite
  // dev-server proxy.
  const fromEnv = (import.meta.env.VITE_LEXSERVER_WS_URL as string | undefined)?.trim();
  if (fromEnv) return fromEnv;
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws`;
}

const SUBPROTOCOL_PREFIX = "lexiun-jwt.";

export function useGateway(options: UseGatewayOptions = {}) {
  const { url, gatewayToken, autoConnect = true } = options;
  // Gateway auth token from Vite env var, falls back to "lexdev" for local dev
  const token = gatewayToken ?? (import.meta.env.VITE_GATEWAY_TOKEN as string | undefined) ?? "lexdev";
  const clientRef = useRef<GatewayClient | null>(null);
  // `crossOriginMode` distinguishes prod (separate-origin lexserver via
  // VITE_LEXSERVER_WS_URL) from same-origin dev (Vite proxy). It only
  // affects URL resolution — both modes fetch the same auth tokens.
  //
  // Post-2026-05-31 retirement of the generic `lexruntime:18789` dev
  // container, dev runs against per-user `lexruntime-<org>-<userId>`
  // containers spawned by lexserver's orchestrator. Those containers
  // hold a per-org deterministic gateway-token (derived from
  // LEXIUN_SIGNING_KEY+orgId) — not the legacy `lexdev` shared secret.
  // So dev MUST follow the same auth flow as prod: (1) routing JWT in
  // subprotocol so lexserver's /ws proxy can resolve the target
  // runtime; (2) per-org gateway-token via /api/agent/connect for the
  // in-band `connect` frame. The earlier `productionMode` branch that
  // skipped both fetches was tied to the retired generic-container
  // topology — removed 2026-05-31. See
  // ai-docs/lexruntime-local-rebuild-and-roll.md.
  const crossOriginMode = Boolean((import.meta.env.VITE_LEXSERVER_WS_URL as string | undefined)?.trim());

  // Use refs for store actions to avoid re-running effect when store updates
  const storeRef = useRef(useGatewayStore.getState());
  storeRef.current = useGatewayStore.getState();

  // We need TWO tokens in BOTH dev and prod before opening the WS:
  //   1. routingToken — Lexiun JWT carried in the WS subprotocol
  //      `lexiun-jwt.<token>` so lexserver's runtime-WS proxy
  //      (runtime-ws-proxy.ts) can resolve which lexruntime to forward to.
  //   2. gatewayToken — per-org shared secret for the in-band `connect`
  //      frame the lexruntime gateway authenticates against.
  const [routingToken, setRoutingToken] = useState<string | null>(null);
  const [fetchedGatewayToken, setFetchedGatewayToken] = useState<string | null>(null);
  // `ready` gates client construction until BOTH tokens are in hand.
  // Equally applied in dev + prod since the auth flow is symmetric.
  const ready = routingToken !== null && fetchedGatewayToken !== null;

  useEffect(() => {
    let cancelled = false;
    const log = clientLog.forSubsystem("gateway");
    void (async () => {
      try {
        const lexiunJwt = await getToken();
        if (cancelled) return;
        setRoutingToken(lexiunJwt);
        // Fetch the per-org gateway token so the in-band `connect` frame
        // authenticates against production lexruntime gateways. The
        // `lexdev` fallback is only valid for local-dev runtimes.
        const res = await fetch("/api/agent/connect", {
          headers: { Authorization: `Bearer ${lexiunJwt}` },
        });
        if (!res.ok) {
          throw new Error(`agent/connect ${res.status}`);
        }
        const body = (await res.json()) as {
          ok?: boolean;
          data?: {
            gatewayToken?: string;
            gatewayTokenError?: string;
            status?: string;
            runtimeState?: RuntimeState | null;
            repairInProgress?: boolean;
          };
        };
        if (cancelled) return;
        // Feature C — surface the runtime lifecycle state for the connection
        // icon + disconnect message (independent of whether the token resolved).
        storeRef.current.setRuntimeStatus(
          body.data?.runtimeState ?? null,
          !!body.data?.repairInProgress,
        );
        const tok = body.data?.gatewayToken;
        if (tok) {
          setFetchedGatewayToken(tok);
          log.info("gateway.token.fetched", { runtime_status: body.data?.status });
        } else {
          const reason = body.data?.gatewayTokenError ?? "agent/connect did not return a gatewayToken";
          storeRef.current.setError(`Gateway token unavailable: ${reason}`);
          log.error("gateway.token.unavailable", { reason });
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : String(err);
          storeRef.current.setError(
            `Could not bootstrap runtime WS: ${message}`,
          );
          log.error("gateway.bootstrap.failed", err instanceof Error ? err : new Error(message));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Run once on mount — Clerk/getToken resolve identically in both
    // dev (same-origin Vite proxy) and prod (cross-origin lexserver).
  }, []);

  useEffect(() => {
    // Defer client creation until BOTH tokens are in hand (dev + prod
    // identical post-2026-05-31).
    if (!ready) return;

    const gatewayUrl = url ?? resolveGatewayUrl();
    // Always pass the routing JWT subprotocol — lexserver's runtime-WS
    // proxy needs it to resolve the target per-user runtime in BOTH
    // dev (same-origin via Vite proxy) and prod (cross-origin).
    const subprotocols = routingToken
      ? [`${SUBPROTOCOL_PREFIX}${routingToken}`]
      : undefined;
    // Always use the fetched per-org gateway token for the in-band
    // `connect.params.auth.token` — the per-user runtime checks this
    // against its `gateway.auth.token` field, which is derived from
    // LEXIUN_SIGNING_KEY+orgId by lexserver's config-sync. The legacy
    // `lexdev` fallback was specific to the retired generic dev
    // container and no longer matches any live runtime.
    const inBandToken = fetchedGatewayToken ?? token;
    const log = clientLog.forSubsystem("gateway");
    // Refresh-on-reconnect callback. Re-fetches the Lexiun JWT
    // (getToken() handles expiry + refresh internally) and the per-org
    // gateway token. Without this, a tab left open overnight reconnects
    // forever with a stale JWT after the ~1h TTL and never recovers
    // until the user manually reloads. See feedback from 2026-05-10.
    // Wired in dev + prod symmetrically since 2026-05-31 — dev now
    // also rides on per-user containers with per-org gateway tokens.
    const refreshAuth = async () => {
      // Session-lost detection: getToken() throws with this exact
      // sentinel from lib/lexiun-jwt.ts when both Clerk session AND
      // the localStorage dev seed are absent — typically because the
      // user signed out in another tab. Stop the reconnect loop
      // immediately so the operational-log isn't spammed for the
      // ~30s window before Clerk's <SignedOut>+<RedirectToSignIn>
      // unmounts us. The redirect handles the user-facing surface.
      let lexiunJwt: string;
      try {
        lexiunJwt = await getToken();
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (msg.includes("no auth source available")) {
          log.warn("gateway.auth.session-lost", { reason: msg });
          clientRef.current?.disconnect();
        }
        throw err;
      }
      const res = await fetch("/api/agent/connect", {
        headers: { Authorization: `Bearer ${lexiunJwt}` },
      });
      if (!res.ok) {
        throw new Error(`agent/connect ${res.status}`);
      }
      const body = (await res.json()) as {
        data?: { gatewayToken?: string };
      };
      const newGatewayToken = body.data?.gatewayToken;
      if (!newGatewayToken) {
        throw new Error("agent/connect did not return gatewayToken");
      }
      log.info("gateway.auth.refreshed", { jwt_len: lexiunJwt.length });
      return {
        token: newGatewayToken,
        subprotocols: [`${SUBPROTOCOL_PREFIX}${lexiunJwt}`],
      };
    };
    const client = new GatewayClient({
      url: gatewayUrl,
      token: inBandToken,
      clientId: "openclaw-control-ui",
      clientMode: "webchat",
      subprotocols,
      refreshAuth,
      onStateChange: (state) => {
        storeRef.current.setConnectionState(state);
        if (state === "connected") {
          storeRef.current.setError(null);
          // A (re)connect can mean the runtime container was ROLLED —
          // its applied model/tier may differ from what the SPA cached.
          // Drop the llm-status cache so the always-running 30s tick in
          // useLlmStatus refetches promptly (operator-observed during the
          // §8.11 acceptance: the composer model chip stayed stale after
          // a roll until a manual reload).
          clearLlmStatusCache();
          // Subscribe this connId to session-scoped events (the server's
          // sessionEventSubscribers registry). Without this RPC, the
          // server NEVER broadcasts `session.tool` EventFrames to us —
          // every tool call (lx_*, ide_*, native update_plan, etc.)
          // would fire serverside but never reach the SPA, breaking the
          // entire reasoning-artifact + platform-ops surfacing pipeline.
          // Re-subscribe on every reconnect (new connId = fresh entry).
          // See lexruntime/openclaw/src/gateway/server-methods/sessions.ts
          // for the server side + lx-todo-implementation-plan.md Phase 5
          // for the discovery context.
          clientRef.current
            ?.request("sessions.subscribe", {})
            .catch((err: unknown) => {
              log.warn("gateway.sessions.subscribe.failed", {
                error: err instanceof Error ? err.message : String(err),
              });
            });
        }
        // Mirror state transitions to the centralized operational
        // log so platform admins can see WS lifecycle for any user
        // session — disconnects, reconnects, hangs all become
        // queryable from the LexPortal Operational Logs viewer.
        const isProblem = state === "reconnecting" || state === "error" || state === "disconnected";
        const fields = { state, gateway_url: gatewayUrl, cross_origin_mode: crossOriginMode };
        if (isProblem) log.warn(`gateway.state.${state}`, fields);
        else log.info(`gateway.state.${state}`, fields);
      },
      onEvent: (event, payload) => {
        dispatchEvent(event, payload);
      },
      onError: (err) => {
        storeRef.current.setError(err.message);
        log.error("gateway.error", err);
      },
      onMaintenance: (active) => {
        storeRef.current.setMaintenance(active);
        log.info(`gateway.maintenance.${active ? "enter" : "exit"}`, {
          gateway_url: gatewayUrl,
        });
      },
    });

    const rpc = createGatewayRpc(client);
    clientRef.current = client;
    storeRef.current.setClient(client, rpc);

    if (autoConnect) {
      client.connect();
    }

    // Visibility-change reconnect: when the tab returns from hidden
    // (laptop wake / overnight idle), force an immediate fresh-auth
    // reconnect instead of waiting up to the 30s backoff ceiling.
    // The OS may have torn the socket down and the in-memory ws state
    // is stale; forceReconnect closes any half-open ws + resets
    // backoff + re-fetches auth. Runs in BOTH dev and production —
    // in dev `refreshAuth` is undefined so reconnectWithRefresh just
    // re-uses the cached "lexdev" creds, which is fine and matches
    // what the regular backoff loop would do anyway.
    const VISIBILITY_WAKE_THRESHOLD_MS = 30_000;
    let lastHiddenAt: number | null = null;
    const onVisibilityChange = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "hidden") {
        lastHiddenAt = Date.now();
      } else if (document.visibilityState === "visible" && lastHiddenAt !== null) {
        const hiddenForMs = Date.now() - lastHiddenAt;
        lastHiddenAt = null;
        // Only force-reconnect if the tab was hidden for longer than
        // the threshold. Short hides (alt-tab, brief minimize) are
        // not worth the auth-refresh overhead and the WS is almost
        // certainly still alive.
        if (hiddenForMs > VISIBILITY_WAKE_THRESHOLD_MS) {
          log.info("gateway.visibility.wake.force-reconnect", { hidden_for_ms: hiddenForMs });
          client.forceReconnect();
        }
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
      client.disconnect();
      clientRef.current = null;
      storeRef.current.clearClient();
    };
  }, [url, gatewayToken, autoConnect, ready, crossOriginMode, routingToken, fetchedGatewayToken]);

  return {
    client: clientRef.current,
    connect: () => clientRef.current?.connect(),
    disconnect: () => clientRef.current?.disconnect(),
  };
}
